import { createLogger } from '@owl-lib/logger';

import type {
  AGENTIC_VERBS,
  Message,
  Summary,
  Thread,
} from '../claims/interface';
import { deserialize, fetchWrapper } from '../fetch';
import type { DocumentCitations } from '../files/interface';
import type { SearchHistory } from './interface';

const logger = createLogger(__filename);

export const TIMEOUT_MS = 300_000;

const searchApiClient = {
  search: (data: {
    dossierId: string;
    query: string;
    dataVersion?: string;
    threadId: string | null;
  }): WebSocket => {
    return new WebSocket(
      new URL(`/app/v1/dossiers/${data.dossierId}/search`, API_BASE_URL).href
    );
  },
  agenticSearch: (data: {
    dossierId: string;
    selected_text: string;
    summary: Summary;
    data_version?: string;
    verbs: AGENTIC_VERBS[];
  }): WebSocket => {
    return new WebSocket(
      new URL(
        `/app/v1/dossiers/${data.dossierId}/agentic_search`,
        API_BASE_URL
      ).href
    );
  },
  getThreads: async (dossierId: string): Promise<{ threads: Thread[] }> => {
    const url = new URL(`/app/v1/${dossierId}/threads`, API_BASE_URL);
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  getMessages: async ({
    dossierId,
    threadId,
  }: {
    dossierId: string;
    threadId: string;
  }): Promise<{ messages: Message[] }> => {
    const url = new URL(
      `/app/v1/${dossierId}/threads/${threadId}/messages`,
      API_BASE_URL
    );
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  deleteThread: async ({
    dossierId,
    threadId,
  }: {
    dossierId: string;
    threadId: string;
  }): Promise<void> => {
    const url = new URL(
      `/app/v1/${dossierId}/threads/${threadId}`,
      API_BASE_URL
    );
    return deserialize(
      await fetchWrapper(url.href, { method: 'DELETE', credentials: 'include' })
    );
  },
  fetchHistory: async (data: {
    dossierId: string;
  }): Promise<SearchHistory[]> => {
    const url = new URL(
      `/app/v1/dossiers/${data.dossierId}/search/history`,
      API_BASE_URL
    );

    logger.trace('searchApiClient.fetchHistory()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
    );
  },
  saveHistory: async (data: {
    dossierId: string;
    email: string;
    query: string;
    answer: string;
    citations: DocumentCitations;
  }): Promise<{ history_id: string }> => {
    const { dossierId, email, query, answer, citations } = data;
    const url = new URL(
      `/app/v1/dossiers/${dossierId}/search/history`,
      API_BASE_URL
    );
    url.searchParams.set('email', email);
    logger.trace('searchApiClient.saveHistory()');
    return deserialize(
      await fetchWrapper(url.href, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-type': 'application/json' },
        body: JSON.stringify({
          query,
          answer,
          citations,
        }),
      })
    );
  },
  saveQuestion: async (data: {
    dossierId: string;
    historyId: string;
    isSaved: boolean;
  }) => {
    const { dossierId, historyId, isSaved } = data;
    const url = new URL(
      `/app/v1/dossiers/${dossierId}/search/history/${historyId}`,
      API_BASE_URL
    );
    url.searchParams.set('is_saved', isSaved.toString());

    logger.trace('searchApiClient.saveQuestion()');
    return deserialize(
      await fetchWrapper(url.href, { method: 'PUT', credentials: 'include' })
    );
  },
};

export default searchApiClient;
